import * as XLSX from 'xlsx';
import { get } from 'lodash';
import { Table } from '@tanstack/react-table';
import {
  FieldDefinitionDto,
  TableDefinitionDto,
} from '@/components/table/types/TableDefinitionDto.ts';
import { fieldsConfig } from '@/components/table/_fieldDefinitions/fieldsConfig.ts';
import { FieldType } from '@/components/table/_fieldDefinitions/FieldType.ts';
import { DropdownFieldDefinitionDto } from '@/components/table/_fieldDefinitions/dropdown/DropdownFieldDefinitionDto.ts';
import { multiselectExportRender } from '@/components/table/_fieldDefinitions/exportRenders.ts';
import { EnumFieldDefinitionDto } from '@/components/table/_fieldDefinitions/enum/EnumFieldDefinitionDto.ts';
import { fieldDisplayNames } from '@/components/table/_fieldDefinitions/fieldDisplayNames.ts';
import {
  getColumnId,
  isVisibleColumn,
} from '@/components/table/preferences/columnPreferences.ts';
import { RowWithId } from '@/components/table/types/RowWithId.ts';
import { getTransactionMoneyRenders } from '@/components/table/_fieldDefinitions/moneyValue/transactionMoneyFieldRenders.tsx';

const ROW_ACTION_UI_COLUMNS = ['select', 'options'];

/** Column header used in the exported sheet. Mirrors `getHeaderDisplayName`. */
function exportHeader(field: FieldDefinitionDto): string {
  if (
    (field.customField || field.fieldId.includes('optionValue')) &&
    field.fieldName
  ) {
    return field.fieldName;
  }
  return fieldDisplayNames[field.fieldId] ?? field.fieldId;
}

/**
 * Path used to read a field's value off a raw row object. Mirrors the
 * accessorKey logic in `useColumns.buildColumns`.
 */
function exportAccessorKey(field: FieldDefinitionDto): string {
  if (field.customField) {
    return `customValues.${field.fieldId.split('.')[1]}`;
  }
  if (field.fieldId.includes('optionValue')) {
    return `selectedOptions.${field.fieldId.split('.')[1]}`;
  }
  return field.fieldId;
}

/** Extra context some export renderers need (dropdown/multiselect/enum). */
function exportContext(field: FieldDefinitionDto): unknown {
  if ([FieldType.DROPDOWN, FieldType.MULTISELECT].includes(field.type)) {
    return { options: (field as DropdownFieldDefinitionDto).options || [] };
  }
  if (field.type === FieldType.ENUM) {
    return { enumSimpleName: (field as EnumFieldDefinitionDto).enumName };
  }
  return undefined;
}

function writeSheet(exportData: Record<string, unknown>[]) {
  const worksheet = XLSX.utils.json_to_sheet(exportData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Dane');
  XLSX.writeFile(
    workbook,
    `export_${new Date().toISOString().slice(0, 10)}.xlsx`,
  );
}

/**
 * Exports an explicit set of rows (e.g. the current selection) to XLSX.
 *
 * Unlike {@link exportToExcel} this does not need a `Table` instance — it reads
 * values straight off the row objects, so it works for rows selected on pages
 * that are no longer rendered.
 *
 * @param orderedFieldIds Visible column ids (see `getColumnId`) in display
 *   order, with the system select/options columns already excluded.
 */
export function exportRowsToExcel<T extends RowWithId>({
  rows,
  tableDefinition,
  orderedFieldIds,
}: {
  rows: T[];
  tableDefinition: TableDefinitionDto;
  orderedFieldIds: string[];
}) {
  const fieldByColumnId = new Map(
    (tableDefinition.fields ?? [])
      .filter(isVisibleColumn)
      .map((field) => [getColumnId(field), field]),
  );

  const columnIds =
    orderedFieldIds.length > 0
      ? orderedFieldIds.filter((id) => fieldByColumnId.has(id))
      : [...fieldByColumnId.keys()];

  const exportData = rows.map((row) => {
    const rowData: Record<string, unknown> = {};
    for (const columnId of columnIds) {
      const field = fieldByColumnId.get(columnId)!;
      const rawValue = get(row, exportAccessorKey(field));
      const exportRender =
        getTransactionMoneyRenders(field)?.exportRender ??
        fieldsConfig[field.type]?.exportRender;
      rowData[exportHeader(field)] = exportRender
        ? exportRender(rawValue, exportContext(field))
        : rawValue;
    }
    return rowData;
  });

  writeSheet(exportData);
}

export function exportToExcel<T>(
  table: Table<T>,
  tableDefinition: TableDefinitionDto,
) {
  const rows = table.getFilteredRowModel().rows;

  const fieldMap = new Map(
    tableDefinition.fields.map((field) => {
      return [field.fieldId, field];
    }),
  );

  const exportData = rows.map((row) => {
    const rowData: Record<string, unknown> = {};

    table.getVisibleLeafColumns().forEach((column) => {
      if (ROW_ACTION_UI_COLUMNS.includes(column.id)) {
        return;
      }

      const header =
        typeof column.columnDef.header === 'string'
          ? column.columnDef.header
          : column.id;

      const rawValue = row.getValue(column.id);
      const fieldDef = fieldMap.get(column.id);

      if (fieldDef) {
        const config = fieldsConfig[fieldDef.type];
        let customFieldsContext: unknown = undefined;

        if (
          [FieldType.DROPDOWN, FieldType.MULTISELECT].includes(fieldDef.type)
        ) {
          const dropdownField = fieldDef as DropdownFieldDefinitionDto;
          customFieldsContext = { options: dropdownField.options || [] };
        }
        if (fieldDef.type === FieldType.ENUM) {
          const enumField = fieldDef as EnumFieldDefinitionDto;
          customFieldsContext = { enumSimpleName: enumField.enumName };
        }
        const exportRender =
          getTransactionMoneyRenders(fieldDef)?.exportRender ??
          config?.exportRender;
        if (exportRender === undefined) {
          console.warn(
            'No export renderer defined for field type - fallback to cellen renderer ',
            fieldDef.type,
          );
          rowData[header] = rawValue;
        } else {
          rowData[header] = exportRender(rawValue, customFieldsContext);
        }
      } else {
        console.warn(
          'No field definition found for column - fallback to raw value for col: ',
          column.id,
        );
        rowData[header] = rawValue;
      }
    });

    return rowData;
  });

  writeSheet(exportData);
}
